import { createSelector } from '@reduxjs/toolkit';
import { selectFeatureFlags } from '@ticketmaster/tm1pos-web-shared/feature-flags';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCheckoutRouter } from '../../../../hooks/use-checkout-router';
import { checkout as legacyCheckout, populateCreditCardData } from '../../actions';
import { CREDIT_CARD_SWIPE_ERROR } from '../../actions-constants';
import { CARD } from '../../constants';
import { selectFormToggles, selectIsCheckoutEnabled } from '../../selectors';
import { CREDIT_CARD_SWIPE_TRACK } from './credit-card-swipe-input-bridge-constants';

declare global {
  interface Window {
    iosCreditCardSwipeBridge: (rawString?: string, err?: string) => boolean;
  }
}

export const parseCreditCardString = (rawString: string) => {
  const match = CREDIT_CARD_SWIPE_TRACK.CC_REG_EX_FORMAT.exec(rawString);
  if (!match) return null;

  const [full, accountNumber, fullName, rawExp] = match;
  const expDate = rawExp.replace(/(\d{2})(\d{2}).*/, '$2/$1');
  const [lastName, firstName] = fullName.trim().split('/');
  const cardHolderName = `${firstName} ${lastName}`;
  CREDIT_CARD_SWIPE_TRACK.CC_REG_EX_FORMAT.lastIndex = 0;
  return { accountNumber, expDate, full, cardHolderName };
};

export const checkKeyForErrors = (key: string, newInputPart: string) =>
  key === CREDIT_CARD_SWIPE_TRACK.ERROR_START && CREDIT_CARD_SWIPE_TRACK.CC_REG_EX_START.test(newInputPart);

export const checkForCompleteData = (rawString: string) =>
  CREDIT_CARD_SWIPE_TRACK.CC_REG_EX_NEEDED_INPUT.test(rawString);

export const inactiveIosCreditCardSwipeBridge = () => false;
window.iosCreditCardSwipeBridge = inactiveIosCreditCardSwipeBridge;

export const CreditCardSwipeInputBridge = () => {
  const dispatch = useDispatch();
  const { manualCardEntry, paymentMethod } = useSelector(
    createSelector(selectFormToggles, (formToggles) => formToggles),
  );
  const isCheckoutEnabled = useSelector(selectIsCheckoutEnabled);
  const [error, setError] = useState('');
  const creditCardInputRef = useRef('');
  const justSwipedRef = useRef(false);
  const onKeyPressRef = useRef<(event: KeyboardEvent) => boolean>();

  const clearCreditCardInputState = () => {
    creditCardInputRef.current = '';
  };

  const handleKeyInput = (newInputPart: string) => {
    if (CREDIT_CARD_SWIPE_TRACK.CC_REG_EX_START.test(newInputPart)) {
      creditCardInputRef.current = newInputPart;
    } else {
      clearCreditCardInputState();
    }
  };

  const flags = useSelector(selectFeatureFlags);
  const { checkout } = useCheckoutRouter();

  const completeSwipe = (rawString: string) => {
    const data = parseCreditCardString(rawString);

    if (data) {
      dispatch(populateCreditCardData(data));
      clearCreditCardInputState();
      if (flags.checkoutRouter) {
        checkout().then();
      } else {
        dispatch(legacyCheckout());
      }
      justSwipedRef.current = true;
      // Wait in case double swipes happen
      setTimeout(() => {
        justSwipedRef.current = false;
      }, 2000);
    }
  };

  const initiateError = () => {
    clearCreditCardInputState();
    setError('Can not read card data');
    dispatch({ type: CREDIT_CARD_SWIPE_ERROR, error: CREDIT_CARD_SWIPE_ERROR });
  };

  // Update the ref with latest values
  useEffect(() => {
    onKeyPressRef.current = (event: KeyboardEvent) => {
      if (justSwipedRef.current) {
        clearCreditCardInputState();
        return false;
      }

      if (!manualCardEntry && paymentMethod === CARD) {
        let newInputPart = creditCardInputRef.current + event.key;

        if (CREDIT_CARD_SWIPE_TRACK.CC_REG_EX_START.test(newInputPart)) {
          event.stopImmediatePropagation();
        }

        if (checkKeyForErrors(event.key, newInputPart)) {
          initiateError();
          return false;
        }

        if (event.key === '%') {
          newInputPart = '%';
        }

        if (checkForCompleteData(newInputPart) && isCheckoutEnabled) {
          completeSwipe(newInputPart);
          return false;
        }

        handleKeyInput(newInputPart);
        return false;
      }

      return true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckoutEnabled, manualCardEntry, paymentMethod]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => onKeyPressRef.current?.(event);
    window.document.addEventListener('keypress', handleKeyPress, true);

    // bridge for mobile app
    window.iosCreditCardSwipeBridge = (rawString?: string, err?: string) => {
      if (rawString) {
        handleKeyInput(rawString);
        onKeyPressRef.current?.({
          key: '',
          stopImmediatePropagation: () => {},
        } as KeyboardEvent);
      }

      if (err) {
        setError(err);
      }

      return true;
    };

    return () => {
      window.document.removeEventListener('keypress', handleKeyPress, true);
      window.iosCreditCardSwipeBridge = inactiveIosCreditCardSwipeBridge;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return error.length ? <div>{error}</div> : null;
};

export default CreditCardSwipeInputBridge;
